import type { ChatRequest, StreamChunk } from './types'
import { TOOL_DEFINITIONS, executeTool, type ToolContext } from './tools'
import { logger } from './logger'

const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions'
const MAX_STEPS = 10

async function callOpenRouter(messages: any[], stream: boolean, apiKey: string, model: string, signal?: AbortSignal): Promise<Response> {
  const body: any = {
    model,
    messages,
    stream,
    temperature: 0.7,
    max_tokens: 16384,
    include_reasoning: true,
    tools: TOOL_DEFINITIONS,
  }

  logger.debug('OpenRouter API call', { model, stream, hasTools: !!body.tools, messagesCount: messages.length })
  return fetch(OPENROUTER_API_URL, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
      'HTTP-Referer': 'http://localhost:5173',
      'X-Title': 'Islam Preacher - The Debater',
    },
    body: JSON.stringify(body),
    signal,
  })
}

function parseSSELine(line: string): { type: string; data?: string } {
  if (line.startsWith('data: ')) {
    const d = line.slice(6).trim()
    if (d === '[DONE]') return { type: 'done' }
    return { type: 'data', data: d }
  }
  return { type: 'skip' }
}

export async function streamChat(
  request: ChatRequest,
  onChunk: (chunk: StreamChunk) => void,
  signal?: AbortSignal,
  apiKey?: string,
  toolContext?: ToolContext
): Promise<void> {
  const key = apiKey || ''
  const { messages, systemPrompt, relevantTranscripts, vectorContext, model } = request

  let systemMessage = systemPrompt || ''

  const multiStepInstruction = `
You can search your knowledge base multiple times to gather comprehensive information.
Use tools to look up specific scriptures, then analyze results before deciding what else to search.
Each search round gives you fresh information — use it to identify what else you need.
When you have enough context, stop making tool calls and answer directly.
You have a maximum of ${MAX_STEPS} search rounds. Be strategic — plan your searches.`

  systemMessage += `\n\n${multiStepInstruction}`
  if (relevantTranscripts) systemMessage += `\n\nHere are relevant transcripts to reference:\n${relevantTranscripts}`
  if (vectorContext) systemMessage += `\n\nHere are relevant scripture references from the vector database:\n${vectorContext}`

  const openRouterMessages: any[] = [
    { role: 'system', content: systemMessage },
    ...messages,
  ]

  const modelId = model || 'qwen/qwen3.5-397b-a17b'

  let totalSteps = 0
  let accumulatedCost = 0

  // === MULTI-STEP AGENTIC LOOP ===
  while (totalSteps < MAX_STEPS) {
    let detectResp: Response
    try {
      detectResp = await callOpenRouter(openRouterMessages, false, key, modelId, signal)
    } catch (err) {
      logger.captureException(err, { context: 'openRouter.detectCall' })
      onChunk({ type: 'error', content: err instanceof Error ? err.message : 'OpenRouter request failed' })
      return
    }

    if (!detectResp.ok) {
      const errText = await detectResp.text().catch(() => '')
      logger.warn('OpenRouter non-ok response', { status: detectResp.status, errText })
      if (detectResp.status === 401) {
        onChunk({ type: 'error', content: 'Invalid OpenRouter API key. Set it in Settings.' })
      } else {
        onChunk({ type: 'error', content: `OpenRouter error ${detectResp.status}: ${errText}` })
      }
      return
    }

    const detectData = await detectResp.json()
    const choice = detectData.choices?.[0]
    const toolCalls = choice?.message?.tool_calls
    accumulatedCost += detectData.usage?.total_cost || 0

    // No tool calls = AI is done researching
    if (!toolCalls || toolCalls.length === 0) {
      const reasoning = choice?.message?.reasoning
      if (reasoning) {
        onChunk({ type: 'thought', content: reasoning })
      }

      const usage = detectData.usage
      if (usage?.prompt_tokens || usage?.completion_tokens) {
        onChunk({
          type: 'usage',
          usage: {
            prompt_tokens: usage.prompt_tokens || 0,
            completion_tokens: usage.completion_tokens || 0,
            total_tokens: usage.total_tokens || 0,
            cost: accumulatedCost,
          },
        })
      }

      if (totalSteps > 0) {
        onChunk({ type: 'tool_step', step: totalSteps, totalSteps: MAX_STEPS, content: 'Producing final answer...' })
        accumulatedCost += await streamFinalAnswer(openRouterMessages, key, modelId, onChunk, signal)
      } else {
        const content = choice?.message?.content || ''
        if (content) {
          const chunkSize = 50
          for (let i = 0; i < content.length; i += chunkSize) {
            if (signal?.aborted) return
            onChunk({ type: 'text', content: content.slice(i, i + chunkSize) })
          }
        }
      }

      onChunk({ type: 'cost', cost: accumulatedCost })
      onChunk({ type: 'done' })
      return
    }

    // Has tool calls — execute them and loop
    totalSteps++
    onChunk({
      type: 'tool_step',
      step: totalSteps,
      totalSteps: MAX_STEPS,
      content: `Search round ${totalSteps}/${MAX_STEPS}`,
    })

    const reasoning = choice?.message?.reasoning
    if (reasoning) {
      onChunk({ type: 'thought', content: reasoning })
    }

    const assistantContent = choice.message?.content || ''
    if (assistantContent) {
      onChunk({ type: 'thought', content: `[Round ${totalSteps} chain-of-thought]\n${assistantContent}` })
    }

    openRouterMessages.push({
      role: 'assistant',
      content: assistantContent,
      tool_calls: toolCalls.map((tc: any) => ({
        id: tc.id,
        type: tc.type,
        function: tc.function,
      })),
    })

    onChunk({
      type: 'tool_call',
      tool_calls: toolCalls.map((tc: any) => ({ name: tc.function.name, arguments: tc.function.arguments })),
    })

    for (const tc of toolCalls) {
      try {
        const args = JSON.parse(tc.function.arguments)
        logger.debug('Executing tool', { tool: tc.function.name, args })
        const result = await executeTool(tc.function.name, args, toolContext)
        openRouterMessages.push({ role: 'tool', tool_call_id: tc.id, content: result })
        logger.debug('Tool result received', { tool: tc.function.name, resultLength: result.length })
      } catch (e) {
        logger.captureException(e, { context: 'toolExecution', tool: tc.function.name })
        openRouterMessages.push({
          role: 'tool',
          tool_call_id: tc.id,
          content: `Error executing ${tc.function.name}: ${e instanceof Error ? e.message : 'Unknown error'}`,
        })
      }
    }

    onChunk({ type: 'tool_result' })
  }

  // === MAX STEPS REACHED — force final answer ===
  onChunk({
    type: 'tool_step',
    step: MAX_STEPS,
    totalSteps: MAX_STEPS,
    content: 'Max search rounds reached. Producing final answer...',
  })
  accumulatedCost += await streamFinalAnswer(openRouterMessages, key, modelId, onChunk, signal)
  onChunk({ type: 'cost', cost: accumulatedCost })
}

async function streamFinalAnswer(
  messages: any[],
  apiKey: string,
  model: string,
  onChunk: (chunk: StreamChunk) => void,
  signal?: AbortSignal
): Promise<number> {
  let streamResp: Response
  try {
    streamResp = await fetch(OPENROUTER_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': 'http://localhost:5173',
        'X-Title': 'Islam Preacher - The Debater',
      },
      body: JSON.stringify({
        model,
        messages,
        stream: true,
        temperature: 0.7,
        max_tokens: 16384,
        include_reasoning: true,
      }),
      signal,
    })
  } catch (err) {
    logger.captureException(err, { context: 'streamFinalAnswer.request' })
    onChunk({ type: 'error', content: err instanceof Error ? err.message : 'Stream request failed' })
    return 0
  }

  if (!streamResp.ok) {
    const errText = await streamResp.text().catch(() => '')
    logger.warn('Stream response not ok', { status: streamResp.status, errText })
    onChunk({ type: 'error', content: `Stream error ${streamResp.status}: ${errText}` })
    return 0
  }

  return await parseSSEStream(streamResp, onChunk, signal)
}

async function parseSSEStream(response: Response, onChunk: (chunk: StreamChunk) => void, signal?: AbortSignal): Promise<number> {
  const reader = response.body?.getReader()
  if (!reader) {
    logger.error('No response body reader available')
    onChunk({ type: 'error', content: 'No response stream' })
    onChunk({ type: 'done' })
    return 0
  }

  const decoder = new TextDecoder()
  let buffer = ''
  let thoughtBuffer = ''
  let streamCost = 0
  let finalUsage: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } | null = null

  while (true) {
    if (signal?.aborted) { reader.cancel(); return streamCost }
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      const parsed = parseSSELine(line)
      if (parsed.type === 'done') {
        if (thoughtBuffer) { onChunk({ type: 'thought', content: thoughtBuffer }); thoughtBuffer = '' }
        onChunk({ type: 'done' })
        continue
      }
      if (parsed.type !== 'data' || !parsed.data) continue

      try {
        const json = JSON.parse(parsed.data)
        if (json.usage) {
          streamCost = json.usage.total_cost || 0
          finalUsage = {
            prompt_tokens: json.usage.prompt_tokens,
            completion_tokens: json.usage.completion_tokens,
            total_tokens: json.usage.total_tokens,
          }
        }
        const delta = json.choices?.[0]?.delta
        if (!delta) continue
        if (delta.reasoning) { thoughtBuffer += delta.reasoning; continue }
        const content = delta.content || ''
        if (!content) continue
        onChunk({ type: 'text', content })
      } catch (e) {
        logger.debug('Failed to parse SSE chunk', { error: e instanceof Error ? e.message : 'parse error' })
      }
    }
  }

  if (finalUsage?.prompt_tokens || finalUsage?.completion_tokens) {
    onChunk({
      type: 'usage',
      usage: {
        prompt_tokens: finalUsage.prompt_tokens || 0,
        completion_tokens: finalUsage.completion_tokens || 0,
        total_tokens: finalUsage.total_tokens || 0,
        cost: streamCost,
      },
    })
  }

  if (thoughtBuffer) onChunk({ type: 'thought', content: thoughtBuffer })
  onChunk({ type: 'done' })
  return streamCost
}
